import { Component, computed, inject, OnInit, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { BusinessTypeService } from '../../services/business';
import { BusinessType } from '../../shared/models/business';
import { CountryService, UserService } from '../../services';
import { Country, User } from '../../shared/models';
import { ApiErrorService, ToastService } from '../../shared/services';
import { GoogleAuthService } from '../../services/google-auth.service';
import { AuthService } from '../../services/auth';
import { PhoneInputComponent } from '../../ui/phone-input/phone-input';

const PASSWORD_RULES: { label: string; test: (value: string) => boolean }[] = [
  { label: 'Al menos 8 caracteres', test: (v) => v.length >= 8 },
  { label: 'Una mayúscula', test: (v) => /[A-Z]/.test(v) },
  { label: 'Una minúscula', test: (v) => /[a-z]/.test(v) },
  { label: 'Un número', test: (v) => /[0-9]/.test(v) },
  { label: 'Un símbolo, como ! o #', test: (v) => /[^a-zA-Z0-9]/.test(v) },
];

function passwordRulesValidator(control: AbstractControl): ValidationErrors | null {
  const value: string = control.value ?? '';
  return PASSWORD_RULES.every((rule) => rule.test(value))
    ? null
    : { passwordRules: true };
}

const STEP_FIELDS: Record<1 | 2, { control: string; elementId: string }[]> = {
  1: [
    { control: 'businessName', elementId: 'businessName' },
    { control: 'businessTypeId', elementId: 'businessType' },
  ],
  2: [
    { control: 'fullName', elementId: 'fullName' },
    { control: 'email', elementId: 'registerEmail' },
    { control: 'phone', elementId: 'phone' },
    { control: 'password', elementId: 'password' },
  ],
};

@Component({
  selector: 'app-register',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    PhoneInputComponent,
  ],
  templateUrl: './register.html',
  styleUrls: ['../auth-form.css', './register.css'],
})
export class Register implements OnInit {
  /** Same rules the API applies on signup, shown live so the user isn't surprised on submit */
  readonly passwordRules = PASSWORD_RULES;

  /** Step 1 asks for the business, step 2 for the person who signs in */
  step = signal<1 | 2>(1);

  //#region Injections
  businessTypeService = inject(BusinessTypeService);
  countriesService = inject(CountryService);
  userService = inject(UserService);
  authService = inject(AuthService);
  googleAuthService = inject(GoogleAuthService);
  router = inject(Router);
  toastService = inject(ToastService);
  apiErrorService = inject(ApiErrorService);
  //#endregion

  //#region Observables
  businessType$ = signal<BusinessType[]>([]);
  countries$ = signal<Country[]>([]);
  //#endregion

  registerForm: FormGroup;
  showPassword = false;
  checkboxToggle = false;
  selectedOption: string = '';
  isOptionSelected: boolean = false;
  isSaving: boolean = false;
  isGoogleSignup: boolean = false;
  googleUserId: string = '';

  ngOnInit(): void {
    this.businessType$ = this.businessTypeService.getStore().getModel();
    this.countries$ = this.countriesService.getStore().getModel();
    this.businessTypeService.fetchAndSetEntitiesToStore();
    this.countriesService.fetchAndSetEntitiesToStore();

    // Check if there's Google user info to pre-fill after a small delay
    setTimeout(() => {
      this.checkForGoogleData();
    }, 100);

    // Also check immediately in case we're already on this page
    this.checkForGoogleData();
  }

  checkForGoogleData(): void {
    const googleUserInfoStr = sessionStorage.getItem('googleUserInfo');

    if (googleUserInfoStr) {
      try {
        const googleUserInfo = JSON.parse(googleUserInfoStr);

        // Verify form is ready
        if (!this.registerForm) {
          setTimeout(() => this.checkForGoogleData(), 500);
          return;
        }

        // Set Google signup flag
        this.isGoogleSignup = true;
        this.googleUserId = googleUserInfo.userId || '';

        // Pre-fill form with Google data - combine firstName + lastName into fullName
        const fullName = [googleUserInfo.firstName, googleUserInfo.lastName]
          .filter(Boolean)
          .join(' ');

        const formData = {
          fullName: fullName,
          email: googleUserInfo.email || '',
        };

        this.registerForm.patchValue(formData);

        // Make fields readonly since they come from Google
        const fullNameControl = this.registerForm.get('fullName');
        const emailControl = this.registerForm.get('email');
        const passwordControl = this.registerForm.get('password');

        if (fullNameControl) {
          fullNameControl.disable();
        }
        if (emailControl) {
          emailControl.disable();
        }
        if (passwordControl) {
          // Remove password requirement for Google users
          passwordControl.clearValidators();
          passwordControl.updateValueAndValidity();
        }

        // Clear the session storage
        sessionStorage.removeItem('googleUserInfo');

        // Show a message to the user
        this.toastService.show(
          'Hemos pre-llenado tu información con los datos de Google. Solo completa los campos faltantes.',
          'info'
        );
      } catch (error) {
        console.error('Error parsing Google user info:', error);
        sessionStorage.removeItem('googleUserInfo');
      }
    }
  }

  /**
   * Splits a full name into firstName and lastName.
   * First word → firstName, rest → lastName.
   * If only one word, lastName = firstName (fallback).
   */
  private splitFullName(fullName: string): {
    firstName: string;
    lastName: string;
  } {
    const trimmed = fullName.trim();
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: parts[0] };
    }
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(' '),
    };
  }

  constructor(private fb: FormBuilder) {
    this.registerForm = this.fb.group({
      businessName: ['', [Validators.required, Validators.minLength(2)]],
      fullName: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      phone: [null, Validators.required], // PhoneInputComponent returns { countryId, phoneNumber } | null
      password: ['', [Validators.required, passwordRulesValidator]],
      businessTypeId: ['', Validators.required],
    });
  }

  /** Enter on step 1 moves to step 2; only step 2 creates the account */
  onFormSubmit() {
    if (this.step() === 1) {
      if (this.focusFirstInvalid(1)) return;
      this.goToStep(2);
      return;
    }
    if (this.focusFirstInvalid(2)) return;
    this.onSubmit();
  }

  goToStep(step: 1 | 2) {
    this.step.set(step);
    // Wait for the step's fields to render before moving focus to the first one
    setTimeout(() => {
      const first = STEP_FIELDS[step].find(
        ({ control }) => this.registerForm.get(control)?.enabled
      );
      if (first) this.focusField(first.elementId);
    });
  }

  /** Marks the step's fields as touched so their errors show, and focuses the first wrong one */
  private focusFirstInvalid(step: 1 | 2): boolean {
    const fields = STEP_FIELDS[step];
    fields.forEach(({ control }) => this.registerForm.get(control)?.markAsTouched());
    const firstInvalid = fields.find(
      ({ control }) => this.registerForm.get(control)?.invalid
    );
    if (firstInvalid) this.focusField(firstInvalid.elementId);
    return !!firstInvalid;
  }

  private focusField(elementId: string) {
    const element = document.getElementById(elementId);
    // The phone field is a component; focus the number input inside it
    const target =
      element?.matches('input, select, textarea')
        ? element
        : element?.querySelector<HTMLElement>('input[formcontrolname="phoneNumber"]');
    target?.focus();
  }

  showError(controlName: string): boolean {
    const control = this.registerForm.get(controlName);
    return !!control && control.invalid && control.touched;
  }

  passwordValue(): string {
    return this.registerForm.get('password')?.value ?? '';
  }

  onSubmit() {
    if (this.registerForm.valid) {
      this.isSaving = true;

      // Get form values, including disabled fields (like email from Google)
      const formValue = this.registerForm.getRawValue();
      const { firstName, lastName } = this.splitFullName(formValue.fullName);

      if (this.isGoogleSignup) {
        // Use Google signup completion endpoint
        const phoneData = formValue.phone as {
          countryId: string;
          phoneNumber: string;
        } | null;
        const completeGoogleSignupData = {
          userId: this.googleUserId,
          phone: phoneData?.phoneNumber || '',
          countryId: phoneData?.countryId || '',
          businessTypeId: formValue.businessTypeId,
          businessName: formValue.businessName,
        };

        this.authService
          .completeGoogleSignup(completeGoogleSignupData)
          .subscribe({
            next: (response) => {
              this.isSaving = false;
              this.authService.login(response);
              this.router.navigate(['/auth/success']);
              this.toastService.show(
                '¡Registro completado! Has iniciado sesión exitosamente.',
                'success'
              );
            },
            error: (err) => {
              this.handleError(err);
            },
          });
      } else {
        // Use regular signup for non-Google users
        const phoneData = formValue.phone as {
          countryId: string;
          phoneNumber: string;
        } | null;
        const user: User = {
          ...formValue,
          firstName,
          lastName,
          phone: phoneData?.phoneNumber || '',
          countryId: phoneData?.countryId || '',
          businessName: formValue.businessName,
        };

        this.userService.post<User>(user).subscribe({
          next: () => {
            this.isSaving = false;
            this.router.navigate(['/auth/success']);
          },
          error: (err) => {
            this.handleError(err);
          },
        });
      }
    } else {
      this.registerForm.markAllAsTouched();
    }
  }

  private handleError(error: unknown) {
    this.isSaving = false;
    this.apiErrorService.handle(error, {
      onValidationErrors: (messages) => {
        this.registerForm.markAllAsTouched();
        const body =
          '<p>Por favor verifica la siguiente información:</p>' +
          messages.map((msg) => `<p>${msg}</p>`).join('');
        this.toastService.show(body, 'warning', 6000);
      },
      onBusinessRuleErrors: (messages) => {
        this.toastService.show(messages.join('<br>'), 'warning', 6000);
      },
      onDefault: (message) => {
        this.toastService.show(message, 'warning', 5000);
      },
    });
  }

  onOptionChange() {
    this.isOptionSelected = this.selectedOption !== '';
  }

  get emailControl(): FormControl {
    return this.registerForm.get('email') as FormControl;
  }

  get passwordControl(): FormControl {
    return this.registerForm.get('password') as FormControl;
  }

  onClick() {
    this.router.navigate(['success']);
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleCheckbox() {
    this.checkboxToggle = !this.checkboxToggle;
  }

  async signInWithGoogle() {
    try {
      // Check if Google SDK is loaded
      if (!this.googleAuthService.isGoogleSdkLoaded()) {
        this.toastService.show(
          'Google SDK no está cargado. Intenta recargar la página.',
          'warning'
        );
        return;
      }

      // Get the token from Google using OAuth2 popup
      const token = await this.googleAuthService.signInWithGoogle();

      // Send the token to our backend for registration/login
      this.authService.authenticateWithGoogle(token).subscribe({
        next: (response) => {
          // Check if user needs to complete signup
          if (response.requiresSignup && response.googleUserInfo) {
            // Store Google data temporarily and redirect to signup form
            sessionStorage.setItem(
              'googleUserInfo',
              JSON.stringify(response.googleUserInfo)
            );

            // Force page reload to ensure data is processed
            window.location.href = '/auth/signup';
            this.toastService.show(
              'Por favor completa tu información de registro.',
              'info'
            );
          } else if (response && response.token && response.expiresAt) {
            // Existing user, login normally
            this.authService.login(response);
            this.router.navigate(['/dashboard']);
            this.toastService.show(
              '¡Bienvenido! Te has iniciado sesión con Google exitosamente.',
              'success'
            );
          } else {
            this.toastService.show(
              'Respuesta inválida del servidor.',
              'warning'
            );
          }
        },
        error: (err: any) => {
          const message =
            err?.error?.message ||
            'Error al registrarse con Google. Intenta de nuevo.';
          console.error('Error en Google Registration:', err);
          this.toastService.show(message, 'warning');
        },
      });
    } catch (error) {
      console.error('Google Sign-Up Error:', error);

      // If the OAuth2 popup fails, show a more specific message
      if (
        error === 'User dismissed Google Sign-In' ||
        error === 'Google authentication failed'
      ) {
        this.toastService.show('Has cancelado el registro con Google.', 'info');
      } else {
        this.toastService.show(
          'Error al registrarse con Google. Intenta de nuevo.',
          'warning'
        );
      }
    }
  }
}
